import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { toast } from "react-hot-toast";
import confetti from "canvas-confetti";
import {
  doc,
  setDoc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  collection,
  addDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { logAuditAction } from "../services/auditService";
import { APP_ID, WEBHOOK_URL, DEFAULT_TEMPLATES } from "../config/constants";
import { generateChecklists, DETECTION_RULES } from "../config/checklistConfig";

// --- TYPES ---

export interface UserType {
  uid: string;
  email: string;
  [key: string]: any;
}

export interface WebDevDetails {
  name: string;
  email: string;
  phone: string;
}

export interface BillingDetails {
  address: string;
  email: string;
  contactPerson: string;
  purchaseOrder: string;
}

export interface MeetingData {
  date: number | string;
  link: string;
  attendeeName: string;
  attendeeEmail: string;
}

export interface Attachment {
  name: string;
  size: string;
  type: string;
  url: string;
}

export interface HandoverData {
  priority: boolean;
  hours: string;
  mcc: string;
  assignee: string;
  assigneeCustom: string;
  endDate: string;
}

export interface ItemMeta {
  user: string;
  time: number;
}

export interface CustomTask {
  id: string;
  label: string;
}

export interface CaseState {
  // Props / Meta
  user: UserType;
  caseId: string;
  currentCaseId: string | null;
  saveStatus: string;
  loading: boolean;

  // Client & Scope
  clientName: string;
  agencyName: string;
  domain: string;
  contactName: string;
  contactEmail: string;
  status: string;
  webDevDetails: WebDevDetails;
  serviceType: string;
  websiteType: "ecommerce" | "leadgen";
  wantsNotion: boolean;
  containerId: string;
  hubspotIds: any;

  // Notes
  notes: string;
  salesNotes: string[];
  attachments: Attachment[];

  // Products & Upsell
  purchasedProducts: any[];
  upsellItems: any[];
  taxEnabled: boolean;
  taxRate: number;

  // Platforms
  platforms: Record<string, boolean>;
  platformIds: Record<string, string>;
  customPlatforms: string[];
  cmps: Record<string, boolean>;
  customCmps: string[];
  customAdvancedScopes: string[];
  customScope: Record<string, boolean>;

  // Checklist
  items: Record<string, boolean>;
  blockedItems: Record<string, boolean>;
  hiddenItems: Record<string, boolean>;
  itemStatus: Record<string, string>;
  showHiddenItems: boolean;
  itemMeta: Record<string, ItemMeta>;
  customTasks: Record<string, CustomTask[]>;

  // Billing
  billingMethod: string;
  billingDetails: BillingDetails;
  chargebeeCustomerId: string;

  // Handover
  notionPageUrl: string | null;
  showHandoverModal: boolean;
  handoverData: HandoverData;
  isSendingHandover: boolean;

  // Meeting
  meeting: MeetingData | null;
  has_meeting: boolean;
  dataClassification: string;

  // Email Generator
  showEmailModal: boolean;

  // Derived / Calculated
  tasks: any; // Return type of generateChecklists
  subtotalOneOff: number;
  subtotalAnnual: number;
  totalGross: number;
  completedCount: number;
  totalCount: number;

  // Metadata
  lastUpdated: any;
}

export interface CaseActions {
  // Navigation
  onBack: () => void;

  // Setters
  setClientName: (val: string) => void;
  setAgencyName: (val: string) => void;
  setDomain: (val: string) => void;
  setContactName: (val: string) => void;
  setContactEmail: (val: string) => void;
  setWebDevDetails: (val: React.SetStateAction<WebDevDetails>) => void;
  setBillingDetails: (val: React.SetStateAction<BillingDetails>) => void;
  setBillingMethod: (val: string) => void;
  setChargebeeCustomerId: (val: string) => void;
  setServiceType: (val: string) => void;
  setWebsiteType: (val: "ecommerce" | "leadgen") => void;
  setWantsNotion: (val: boolean) => void;
  setContainerId: (val: string) => void;
  setTaxEnabled: (val: boolean) => void;

  setPurchasedProducts: (val: any[]) => void;
  setUpsellItems: (val: any[]) => void;

  setPlatforms: (val: React.SetStateAction<Record<string, boolean>>) => void;
  setCmps: (val: React.SetStateAction<Record<string, boolean>>) => void;
  setCustomPlatforms: (val: React.SetStateAction<string[]>) => void;
  setCustomCmps: (val: React.SetStateAction<string[]>) => void;
  setCustomScope: (val: React.SetStateAction<Record<string, boolean>>) => void;
  setCustomAdvancedScopes: (val: React.SetStateAction<string[]>) => void;

  setShowHandoverModal: (val: boolean) => void;
  setHandoverData: (val: React.SetStateAction<HandoverData>) => void;
  setShowEmailModal: (val: boolean) => void;
  setDataClassification: (val: string) => void;

  // Logic
  savePartial: (dataToUpdate: Record<string, any>) => Promise<void>;
  debouncedSave: (key: string, value: any) => void;
  handleTextChange: (setter: any, key: string, value: string) => void;
  logAuditAction: typeof logAuditAction;

  // Checklist Logic
  toggleChecklist: (id: string) => void;
  setBlockedItems: (
    updater: (prev: Record<string, boolean>) => Record<string, boolean>,
  ) => void; // Wrapped for easy use or direct setter
  setHiddenItems: (
    updater: (prev: Record<string, boolean>) => Record<string, boolean>,
  ) => void;
  handleAddCustomTask: (category: string, label: string) => void;
  handleDeleteCustomTask: (category: string, id: string) => void;
  handleUpdatePlatformId: (id: string, value: string) => void;
  handleVerify: (id: string, isVerified: boolean) => void;
  deleteItem: (list: any[], setList: any, idx: number, saveKey: string) => void;

  // Actions
  handleHandoverSubmit: () => Promise<void>;
  generateEmailPreview: (
    lang: "en" | "nl",
    template: "kickoff" | "status" | "handover",
  ) => string;

  // Specific Setters (wrappers if needed or direct exposure)
  setNotes: (val: string) => void;
  setSaveStatus: (val: string) => void; // Needed? Maybe readonly

  // Helper to directly update state and save (used in some children)
  updateBlockedItem: (id: string, val: boolean) => void;
  updateHiddenItem: (id: string, val: boolean) => void;
}

// Combine State and Actions
export interface CaseContextType extends CaseState, CaseActions {}

const CaseContext = createContext<CaseContextType | undefined>(undefined);

export const useCase = () => {
  const context = useContext(CaseContext);
  if (!context) {
    throw new Error("useCase must be used within a CaseProvider");
  }
  return context;
};

interface CaseProviderProps {
  children: ReactNode;
  user: UserType;
  caseId: string;
  onBack: () => void;
}

export const CaseProvider: React.FC<CaseProviderProps> = ({
  children,
  user,
  caseId,
  onBack,
}) => {
  // --- STATE ---
  const [saveStatus, setSaveStatus] = useState("saved");
  const [loading, setLoading] = useState(true);

  // Client & Scope
  const [clientName, setClientName] = useState("");
  const [agencyName, setAgencyName] = useState("");
  const [domain, setDomain] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [status, setStatus] = useState("new");
  const [webDevDetails, setWebDevDetails] = useState<WebDevDetails>({
    name: "",
    email: "",
    phone: "",
  });
  const [serviceType, setServiceType] = useState("implementation");
  const [websiteType, setWebsiteType] = useState<"ecommerce" | "leadgen">(
    "ecommerce",
  );
  const [wantsNotion, setWantsNotion] = useState(true);
  const [containerId, setContainerId] = useState("");
  const [hubspotIds, setHubspotIds] = useState<Record<string, string | number>>({});

  // Notes
  const [notes, setNotes] = useState("");
  const [salesNotes, setSalesNotes] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  // const notesRef = useRef<HTMLTextAreaElement>(null); // Ref for textarea, keep in component if purely UI, but here we only need value

  // Products & Upsell
  const [purchasedProducts, setPurchasedProducts] = useState<any[]>([]);
  const [upsellItems, setUpsellItems] = useState<any[]>([]);
  const [taxEnabled, setTaxEnabled] = useState(true);
  const [taxRate] = useState(21);

  // Platforms
  const [platforms, setPlatforms] = useState<Record<string, boolean>>({
    ga4: false,
    gtm: false,
    google_ads: false,
    meta: false,
    tiktok: false,
    pinterest: false,
    snapchat: false,
    linkedin: false,
  });
  const [platformIds, setPlatformIds] = useState<Record<string, string>>({});
  const [customPlatforms, setCustomPlatforms] = useState<string[]>([]);

  const [cmps, setCmps] = useState<Record<string, boolean>>({});
  const [customCmps, setCustomCmps] = useState<string[]>([]);

  const [customAdvancedScopes, setCustomAdvancedScopes] = useState<string[]>(
    [],
  );

  const [customScope, setCustomScope] = useState<Record<string, boolean>>({
    datalayers: false,
    offline_conv: false,
    bigquery: false,
    firestore: false,
    sheets: false,
  });

  // Checklist
  const [items, setItems] = useState<Record<string, boolean>>({});
  const [blockedItems, setBlockedItems] = useState<Record<string, boolean>>({});
  const [hiddenItems, setHiddenItems] = useState<Record<string, boolean>>({});
  const [itemStatus, setItemStatus] = useState<Record<string, string>>({});
  const [showHiddenItems] = useState(false);
  const [itemMeta, setItemMeta] = useState<Record<string, ItemMeta>>({});
  const [customTasks, setCustomTasks] = useState<Record<string, CustomTask[]>>(
    {},
  );

  // Billing
  const [billingMethod, setBillingMethod] = useState("default");
  const [billingDetails, setBillingDetails] = useState<BillingDetails>({
    address: "",
    email: "",
    contactPerson: "",
    purchaseOrder: "",
  });
  const [chargebeeCustomerId, setChargebeeCustomerId] = useState("");

  // Handover
  const [notionPageUrl, setNotionPageUrl] = useState<string | null>(null);
  const [showHandoverModal, setShowHandoverModal] = useState(false);
  const [handoverData, setHandoverData] = useState<HandoverData>({
    priority: false,
    hours: "",
    mcc: "MCC-1",
    assignee: "Florian",
    assigneeCustom: "",
    endDate: "",
  });
  const [isSendingHandover, setIsSendingHandover] = useState(false);

  const [meeting, setMeeting] = useState<MeetingData | null>(null);
  const [has_meeting, setHasMeeting] = useState(false);
  const [dataClassification, setDataClassification] = useState("internal");

  const [currentCaseId, setCurrentCaseId] = useState<string | null>(
    caseId === "new" ? null : caseId,
  );

  const [lastUpdated, setLastUpdated] = useState<any>(null);

  const initializingRef = useRef(false);

  // Email Generator State
  const [showEmailModal, setShowEmailModal] = useState(false);

  // --- LOGIC: INITIALIZATION ---
  useEffect(() => {
    if (caseId === "new") {
      if (initializingRef.current) return;
      initializingRef.current = true;

      const casesRef = collection(
        db,
        "artifacts",
        APP_ID,
        "public",
        "data",
        "onboarding_cases",
      );
      const newDocRef = doc(casesRef);

      setDoc(newDocRef, {
        created: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        status: "new",
        progress: 0,
        totalItems: 0,
      }).then(() => {
        logAuditAction(
          "CREATE",
          {
            collection: "onboarding_cases",
            id: newDocRef.id,
            name: "New Case",
          },
          { uid: user?.uid || "unknown", email: user?.email || "unknown" },
        );
        setCurrentCaseId(newDocRef.id);
      });
    } else {
      setCurrentCaseId(caseId);
    }
  }, [caseId, user]);

  const saveTimeoutRef = useRef<any>(null);
  const pendingUpdatesRef = useRef<Record<string, any>>({});

  // --- LOGIC: LOGGING & SAVING ---
  const logActivity = async (
    text: string,
    type:
      | "checklist"
      | "status"
      | "handover"
      | "blocked"
      | "info"
      | "note" = "info",
  ) => {
    if (!currentCaseId || !user) return;
    try {
      const logsRef = collection(
        db,
        "artifacts",
        APP_ID,
        "public",
        "data",
        "onboarding_cases",
        currentCaseId,
        "history",
      );
      await addDoc(logsRef, {
        text,
        user: user.email,
        timestamp: serverTimestamp(),
        type,
      });
    } catch (err) {
      console.error("Error logging activity:", err);
    }
  };

  const savePartial = async (dataToUpdate: Record<string, any>) => {
    if (!currentCaseId || !db) return;
    setSaveStatus("saving");
    try {
      // Version History Logging for Notes
      if ("notes" in dataToUpdate) {
        logActivity(dataToUpdate.notes, "note");
      }

      await updateDoc(
        doc(
          db,
          "artifacts",
          APP_ID,
          "public",
          "data",
          "onboarding_cases",
          currentCaseId,
        ),
        { ...dataToUpdate, lastUpdated: serverTimestamp() },
      );
      setSaveStatus("saved");
    } catch (error) {
      setSaveStatus("error");
    }
  };

  const debouncedSave = useCallback(
    (key: string, value: any) => {
      // 1. Accumulate changes
      pendingUpdatesRef.current = {
        ...pendingUpdatesRef.current,
        [key]: value,
      };

      // 2. Clear previous timer
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      setSaveStatus("modified");

      // 3. Set new timer to save ALL pending changes
      saveTimeoutRef.current = setTimeout(() => {
        const updatesToSave = { ...pendingUpdatesRef.current };
        pendingUpdatesRef.current = {}; // Clear pending *before* async save starts (or after? before is safer to avoid double saving if rapid fire)
        savePartial(updatesToSave);
      }, 1000);
    },
    [currentCaseId],
  );

  const handleTextChange = (setter: any, key: string, value: string) => {
    setter(value);
    debouncedSave(key, value);
  };

  // --- LOGIC: SYNC DATA ---
  const syncCaseData = (c: any) => {
    const isFocused = (id: string) => document.activeElement?.id === id;
    if (!isFocused("clientNameInput")) setClientName(c.clientName || "");
    if (!isFocused("agencyNameInput")) setAgencyName(c.agencyName || "");
    if (!isFocused("domainInput")) setDomain(c.domain || "");
    if (!isFocused("containerIdInput")) setContainerId(c.containerId || "");

    if (!isFocused("contactNameInput")) {
      // NOTE: We primarily use 'contactName' in the app state and DB.
      // But some legacy or dashboard views might look for 'contactPerson'.
      // We sync whatever is available into our local 'contactName' state.
      const resolvedName =
        c.contactName ||
        c.contactPerson ||
        c.billing?.details?.contactPerson ||
        "";
      setContactName(resolvedName);
    }
    if (!isFocused("contactEmailInput")) {
      const resolvedEmail = c.contactEmail || c.billing?.details?.email || "";
      setContactEmail(resolvedEmail);
    }

    if (!isFocused("notesInput")) setNotes(c.notes || "");

    if (c.lastUpdated) setLastUpdated(c.lastUpdated);

    if (c.webDevDetails) {
      if (!isFocused("webDevName"))
        setWebDevDetails((prev) => ({ ...prev, name: c.webDevDetails.name }));
      if (!isFocused("webDevEmail"))
        setWebDevDetails((prev) => ({ ...prev, email: c.webDevDetails.email }));
    }
    if (c.billing?.details) {
      if (!isFocused("billingContact"))
        setBillingDetails((prev) => ({
          ...prev,
          contactPerson: c.billing.details.contactPerson,
        }));
      if (!isFocused("billingEmail"))
        setBillingDetails((prev) => ({
          ...prev,
          email: c.billing.details.email,
        }));
      if (!isFocused("billingAddress"))
        setBillingDetails((prev) => ({
          ...prev,
          address: c.billing.details.address,
        }));
    }

    if (c.status) setStatus(c.status);
    setServiceType(c.serviceType || "implementation");
    setWebsiteType(c.websiteType || "ecommerce");
    setBillingMethod(c.billing?.method || "default");
    setChargebeeCustomerId(c.chargebeeCustomerId || "");
    setWantsNotion(c.wantsNotion !== undefined ? c.wantsNotion : true);
    setDataClassification(c.dataClassification || "internal");

    setPlatforms(c.platforms || {});
    setCmps(c.cmps || {});
    setCustomPlatforms(c.customPlatforms || []);
    setCustomCmps(c.customCmps || []);
    setPurchasedProducts(c.purchasedProducts || []);
    setUpsellItems(c.upsellItems || []);

    setHubspotIds(c.hubspotIds || {});

    setCustomScope(c.customScope || {});
    setCustomAdvancedScopes(c.customAdvancedScopes || []);
    setPlatformIds(c.platformIds || {});

    setItems(c.items || {});
    setBlockedItems(c.blockedItems || {});
    setHiddenItems(c.hiddenItems || {});
    setItemStatus(c.itemStatus || {});
    setItemMeta(c.itemMeta || {});
    setCustomTasks(c.customTasks || {});
    if (c.dealNotes) setSalesNotes(c.dealNotes);
    if (c.attachments) setAttachments(c.attachments);
    if (c.notionPageUrl) setNotionPageUrl(c.notionPageUrl);
    if (c.meeting) {
      setMeeting({
        date: c.meeting.meeting_date || c.meeting.date,
        link: c.meeting.meeting_link || c.meeting.link,
        attendeeName: c.meeting.attendee_name || c.meeting.attendeeName,
        attendeeEmail: c.meeting.attendee_email || c.meeting.attendeeEmail,
      });
      setHasMeeting(true);
    } else {
      setMeeting(null);
      setHasMeeting(false);
    }
  };

  useEffect(() => {
    if (!currentCaseId || !db || currentCaseId === "new") return;
    const docRef = doc(
      db,
      "artifacts",
      APP_ID,
      "public",
      "data",
      "onboarding_cases",
      currentCaseId,
    );
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      setLoading(false);
      if (docSnap.exists()) {
        syncCaseData(docSnap.data());
      } else {
        setDoc(docRef, {
          created: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          progress: 0,
          totalItems: 0,
          status: "new",
        });
      }
    });
    return () => unsubscribe();
  }, [currentCaseId]);

  // --- AUTOMATIC THEME LOGIC ---
  useEffect(() => {
    const root = document.documentElement;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    if (mediaQuery.matches) root.classList.add("dark");
    else root.classList.remove("dark");

    const handleChange = (e: MediaQueryListEvent) => {
      if (e.matches) root.classList.add("dark");
      else root.classList.remove("dark");
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // --- AUTO DETECT PLATFORMS ---
  useEffect(() => {
    if (
      !purchasedProducts ||
      !Array.isArray(purchasedProducts) ||
      purchasedProducts.length === 0
    )
      return;

    const fullText = purchasedProducts
      .map((p) => `${p.name} ${p.description || ""}`)
      .join(" ")
      .toLowerCase();

    const newPlatforms = { ...platforms };
    const newCmps = { ...cmps };
    let hasPlatformUpdates = false;

    const enableIfFound = (
      key: string,
      patterns: RegExp[],
      targetObj: Record<string, boolean>,
    ) => {
      if (!targetObj[key] && patterns.some((regex) => regex.test(fullText))) {
        targetObj[key] = true;
        hasPlatformUpdates = true;
      }
    };

    Object.keys(DETECTION_RULES).forEach((key) => {
      if (
        [
          "ga4",
          "gtm",
          "google_ads",
          "meta",
          "tiktok",
          "pinterest",
          "snapchat",
          "linkedin",
        ].includes(key)
      )
        enableIfFound(key, DETECTION_RULES[key], newPlatforms);
      if (["cookiebot", "cookieyes", "iubenda", "axeptio"].includes(key))
        enableIfFound(key, DETECTION_RULES[key], newCmps);
    });

    let detectedServiceType: string | null = null;
    if (fullText.includes("act - sst check")) {
      detectedServiceType = "audit";
    } else if (fullText.includes("act - server side implementation")) {
      detectedServiceType = "implementation";
    } else if (fullText.includes("act - additional work")) {
      detectedServiceType = "additional";
    }

    const updates: any = {};
    if (hasPlatformUpdates) {
      setPlatforms(newPlatforms);
      setCmps(newCmps);
      updates.platforms = newPlatforms;
      updates.cmps = newCmps;
    }

    if (detectedServiceType && detectedServiceType !== serviceType) {
      setServiceType(detectedServiceType);
      updates.serviceType = detectedServiceType;
    }

    if (Object.keys(updates).length > 0) {
      savePartial(updates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchasedProducts]);

  useEffect(() => {
    if (serviceType === "audit" && wantsNotion) {
      setWantsNotion(false);
      savePartial({ wantsNotion: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceType]);

  // --- CALCULATIONS ---
  const parsePrice = (price: any) =>
    parseFloat(String(price).replace(/[^0-9.-]+/g, "")) || 0;

  const { subtotalOneOff, subtotalAnnual, totalGross } = useMemo(() => {
    let subtotalOneOff = 0,
      subtotalAnnual = 0;
    purchasedProducts.forEach((p) => {
      const lineTotal = parsePrice(p.price) * (parseFloat(p.quantity) || 1);
      const freq = p.billingFrequency ? p.billingFrequency.toLowerCase() : "";
      if (freq.includes("annual")) subtotalAnnual += lineTotal;
      else subtotalOneOff += lineTotal;
    });
    const totalNet = subtotalOneOff + subtotalAnnual;
    const vat = taxEnabled ? totalNet * (taxRate / 100) : 0;
    return { subtotalOneOff, subtotalAnnual, vat, totalGross: totalNet + vat };
  }, [purchasedProducts, taxEnabled, taxRate]);

  // --- TASKS ---
  const tasks = useMemo(
    () =>
      generateChecklists({
        wantsNotion,
        containerId,
        serviceType,
        cmps,
        websiteType,
        platforms,
        customScope,
        customTasks,
        customCmps,
        customPlatforms,
      }),
    [
      wantsNotion,
      containerId,
      serviceType,
      cmps,
      websiteType,
      platforms,
      customScope,
      customTasks,
      customCmps,
      customPlatforms,
    ],
  );

  const findTaskLabel = (id: string) => {
    const all = [
      ...(tasks.admin || []),
      ...(tasks.infrastructure || []),
      ...(tasks.website || []),
      ...(tasks.platforms || []),
      ...(tasks.advanced || []),
      ...(tasks.custom || []),
    ];
    return all.find((t: any) => t.id === id)?.label || id;
  };

  // --- ACTIONS ---
  const toggleChecklist = (id: string) => {
    const isChecked = !items[id];
    const newItems = { ...items, [id]: isChecked };
    setItems(newItems);

    const payload: any = { [`items.${id}`]: isChecked };
    if (isChecked && user) {
      const meta = {
        user: user.email?.split("@")[0] || "User",
        time: Date.now(),
      };
      const newMeta = { ...itemMeta, [id]: meta };
      setItemMeta(newMeta);
      payload[`itemMeta.${id}`] = meta;
    }
    savePartial(payload);

    const label = findTaskLabel(id);
    logActivity(
      `${isChecked ? "Completed" : "Unchecked"}: ${label}`,
      "checklist",
    );
  };

  const handleAddCustomTask = (category: string, label: string) => {
    const newId = `task_${category}_${Date.now()}`;
    const newTask = { id: newId, label };
    const updatedCategoryTasks = [...(customTasks[category] || []), newTask];
    const updatedTasks = { ...customTasks, [category]: updatedCategoryTasks };
    setCustomTasks(updatedTasks);
    savePartial({ customTasks: updatedTasks });
  };

  const handleDeleteCustomTask = (category: string, id: string) => {
    const updatedCategoryTasks = (customTasks[category] || []).filter(
      (t: any) => t.id !== id,
    );
    const updatedTasks = { ...customTasks, [category]: updatedCategoryTasks };
    setCustomTasks(updatedTasks);
    savePartial({ customTasks: updatedTasks });
  };

  const deleteItem = (
    list: any[],
    setList: any,
    idx: number,
    saveKey: string,
  ) => {
    if (window.confirm("Remove item?")) {
      const u = [...list];
      u.splice(idx, 1);
      setList(u);
      savePartial({ [saveKey]: u });
    }
  };

  const handleUpdatePlatformId = (id: string, value: string) => {
    setPlatformIds((prev) => ({ ...prev, [id]: value }));
    debouncedSave(`platformIds.${id}`, value);
  };

  const handleVerify = (id: string, isVerified: boolean) => {
    const newStatus = isVerified ? "verified" : null;
    setItemStatus((prev) => ({ ...prev, [id]: newStatus as string }));
    savePartial({ [`itemStatus.${id}`]: newStatus });

    const label = findTaskLabel(id);
    logActivity(`Verified item: ${label}`, "checklist");
  };

  // --- HANDOVER ---
  const handleHandoverSubmit = async () => {
    setIsSendingHandover(true);

    const payload = {
      caseId: currentCaseId,
      clientName,
      domain,
      hubspotIds: hubspotIds || {},
      contactName,
      contactEmail,
      handoverData,
      items: purchasedProducts,
      user: user.email,
      timestamp: new Date().toISOString(),
      invoiceAmount: totalGross,
      notes: notes,
      containerId,
      serviceType,
    };

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        if (result && result.url) setNotionPageUrl(result.url);

        confetti();
        toast.success("Handover Successful! Notion page created.");
        setShowHandoverModal(false);
        savePartial({ status: "transferred", notionPageUrl: result.url || "" });
        logAuditAction(
          "STATUS_CHANGE",
          {
            collection: "onboarding_cases",
            id: currentCaseId || "",
            name: clientName || "Unknown",
          },
          { uid: user?.uid || "unknown", email: user?.email || "unknown" },
          [{ field: "status", oldValue: status, newValue: "transferred" }],
          { handoverUrl: result.url || "" },
        );
      } else {
        const text = await response.text();
        console.error("Handover response error:", text);
        toast.error("Handover failed. Check webhook connection.");
      }
    } catch (error) {
      console.error("Handover error:", error);
      toast.error("Network error during handover. Check console.");
    } finally {
      setIsSendingHandover(false);
    }
  };

  // --- PROGRESS ---
  const { completedCount, totalCount } = useMemo(() => {
    const allLists = [
      ...(tasks.admin || []),
      ...(tasks.infrastructure || []),
      ...(tasks.website || []),
      ...(tasks.platforms || []),
      ...(tasks.advanced || []),
      ...(tasks.custom || []),
    ];

    let t = 0;
    let c = 0;

    allLists.forEach((item: any) => {
      if (!hiddenItems[item.id]) {
        t++;
        if (items[item.id]) c++;
      }
    });
    return { completedCount: c, totalCount: t };
  }, [tasks, items, hiddenItems]);

  useEffect(() => {
    if (!currentCaseId || status === "transferred") return;

    // If meeting is scheduled, check if it's still in the future
    if (status === "meeting_scheduled") {
      let meetingTime = 0;
      if (meeting?.date) {
        if (typeof meeting.date === "number") meetingTime = meeting.date;
        else if (typeof meeting.date === "string")
          meetingTime = new Date(meeting.date).getTime();
      }

      // If meeting is in the future (or invalid), don't auto-change status based on progress
      if (meetingTime > Date.now()) {
        return;
      }
      // If meeting has passed, allow fall-through to calculatedStatus logic below
    }

    const hasBlockedItems = Object.values(blockedItems).some(
      (val) => val === true,
    );
    const hasProgress = completedCount > 0;

    let calculatedStatus = status;

    if (hasBlockedItems) {
      calculatedStatus = "on_hold";
    } else if (hasProgress) {
      calculatedStatus = "busy";
    } else {
      // If we just came from meeting_scheduled (time passed), we default to busy usually,
      // but if 0 progress, maybe 'new'?
      // However, usually 'In Progress' is preferred after meeting.
      // If 0 progress and not blocked -> 'new'.
      // If the user wants "Meeting Started" -> "In Progress" regardless of checks:
      if (status === "meeting_scheduled") {
        calculatedStatus = "busy";
      } else {
        calculatedStatus = "new";
      }
    }

    if (calculatedStatus !== status) {
      setStatus(calculatedStatus);
      debouncedSave("status", calculatedStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedCount, blockedItems, status, currentCaseId, meeting]);

  useEffect(() => {
    if (!currentCaseId || !db) return;
    const timeoutId = setTimeout(() => {
      const docRef = doc(
        db,
        "artifacts",
        APP_ID,
        "public",
        "data",
        "onboarding_cases",
        currentCaseId,
      );
      updateDoc(docRef, {
        progress: completedCount,
        totalItems: totalCount,
      }).catch(console.error);
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [completedCount, totalCount, currentCaseId]);

  // --- CONFETTI ---
  const allowConfetti = useRef(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      allowConfetti.current = true;
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (
      allowConfetti.current &&
      totalCount > 0 &&
      completedCount === totalCount
    ) {
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: ["#2563eb", "#3b82f6", "#60a5fa", "#ffffff"],
      });
    }
  }, [completedCount, totalCount]);

  // --- EMAIL GENERATOR ---
  const generateEmailPreview = (
    lang: "en" | "nl",
    template: "kickoff" | "status" | "handover",
  ) => {
    const allTasks = [
      ...tasks.admin,
      ...tasks.infrastructure,
      ...tasks.website,
      ...tasks.platforms,
      ...tasks.advanced,
      ...tasks.custom,
    ];

    const getList = (condition: (id: string) => boolean) => {
      return allTasks
        .filter((t) => condition(t.id))
        .map((t) => `- ${t.label}`)
        .join("\n");
    };

    const missing = getList(
      (id) => !items[id] && !blockedItems[id] && !hiddenItems[id],
    );
    const blocked = getList((id) => blockedItems[id]);
    const completed = getList((id) => items[id]);

    const progress = Math.round(
      (Object.values(items).filter(Boolean).length / (allTasks.length || 1)) *
        100,
    );

    // @ts-ignore
    let text = DEFAULT_TEMPLATES[lang]?.[template] || "";

    return text
      .replace(/{{client_first_name}}/g, contactName.split(" ")[0] || "Client")
      .replace(
        /{{service_type}}/g,
        serviceType === "implementation" ? "Server Side Tracking" : "Audit",
      )
      .replace(/{{progress_percentage}}/g, String(progress))
      .replace(/{{missing_items_list}}/g, missing || "(None)")
      .replace(/{{blocked_items_list}}/g, blocked || "(None)")
      .replace(/{{completed_items_list}}/g, completed || "(None)")
      .replace(
        /{{billing_info_block}}/g,
        billingMethod === "manual"
          ? `Billing Address:\n${billingDetails.address}`
          : "",
      );
  };

  // New helpers for child components to easily update blocked/hidden without full object construction if they prefer
  const updateBlockedItem = (id: string, val: boolean) => {
    setBlockedItems((prev) => {
      const newVal = { ...prev, [id]: val };
      // We also need to save
      // NOTE: The original code did savePartial inside the click handler of the child.
      // We should replicate that or provide a way to do it.
      // The original used: setBlockedItems({ ...blockedItems, [id]: !blockedItems[id] }); savePartial(...)
      // So here we can just do:
      savePartial({ [`blockedItems.${id}`]: val });
      return newVal;
    });
  };

  const updateHiddenItem = (id: string, val: boolean) => {
    setHiddenItems((prev) => {
      const newVal = { ...prev, [id]: val };
      savePartial({ [`hiddenItems.${id}`]: val });
      return newVal;
    });
  };

  const value: CaseContextType = {
    user,
    caseId,
    currentCaseId,
    saveStatus,
    loading,
    clientName,
    agencyName,
    domain,
    contactName,
    contactEmail,
    status,
    webDevDetails,
    serviceType,
    websiteType,
    wantsNotion,
    containerId,
    hubspotIds,
    notes,
    salesNotes,
    attachments,
    purchasedProducts,
    upsellItems,
    taxEnabled,
    taxRate,
    platforms,
    platformIds,
    customPlatforms,
    cmps,
    customCmps,
    customAdvancedScopes,
    customScope,
    items,
    blockedItems,
    hiddenItems,
    itemStatus,
    showHiddenItems,
    itemMeta,
    customTasks,
    billingMethod,
    billingDetails,
    chargebeeCustomerId,
    notionPageUrl,
    showHandoverModal,
    handoverData,
    isSendingHandover,
    meeting,
    has_meeting,
    dataClassification,
    showEmailModal,
    tasks,
    subtotalOneOff,
    subtotalAnnual,
    totalGross,
    completedCount,
    totalCount,
    onBack,
    setClientName,
    setAgencyName,
    setDomain,
    setContactName,
    setContactEmail,
    setWebDevDetails,
    setBillingDetails,
    setBillingMethod,
    setChargebeeCustomerId,
    setServiceType,
    setWebsiteType,
    setWantsNotion,
    setContainerId,
    setTaxEnabled,
    setPurchasedProducts,
    setUpsellItems,
    setPlatforms,
    setCmps,
    setCustomPlatforms,
    setCustomCmps,
    setCustomScope,
    setCustomAdvancedScopes,
    setShowHandoverModal,
    setHandoverData,
    setShowEmailModal,
    setDataClassification,
    savePartial,
    debouncedSave,
    handleTextChange,
    logAuditAction,
    toggleChecklist,
    setBlockedItems,
    setHiddenItems,
    handleAddCustomTask,
    handleDeleteCustomTask,
    handleUpdatePlatformId,
    handleVerify,
    deleteItem,
    handleHandoverSubmit,
    generateEmailPreview,
    setNotes,
    setSaveStatus,
    updateBlockedItem,
    updateHiddenItem,
    lastUpdated,
  };

  return <CaseContext.Provider value={value}>{children}</CaseContext.Provider>;
};
